import axios from 'axios';
import { Message } from 'element-ui';
import { OA_BASE_URL, getOaHeaders } from '../config/oa';

// 为了让请求 URL 明确为配置的 OA 后端地址，每次显式拼接 OA_BASE_URL
function requestOA(config) {
  const headers = config.headers || {};
  const url = `${OA_BASE_URL}${config.url}`;
  return axios({
    method: config.method || 'get',
    url,
    params: config.params, // 关键：透传查询参数，例如 formCode
    data: config.data,
    headers,
    timeout: 1000 * 30,
  })
    .then(res => res.data)
    .catch(err => {
      if (!config.silent) {
        Message.error(err.message || 'OA 接口请求失败');
      }
      throw err;
    });
}

function normalizeOaResponse(resp) {
  if (resp && typeof resp === 'object' && Object.prototype.hasOwnProperty.call(resp, 'code')) {
    return resp;
  }

  return {
    code: 200,
    data: resp,
  };
}

function normalizeListResponse(resp) {
  const normalized = normalizeOaResponse(resp);
  const payload = normalized.data;

  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload && payload.data)
      ? payload.data
      : Array.isArray(resp && resp.data)
        ? resp.data
        : [];

  const pagination = normalized.pagination || (payload && payload.pagination) || (resp && resp.pagination) || {};

  return {
    code: normalized.code,
    msg: normalized.msg,
    data: list,
    pagination,
  };
}

// 获取启动流程所需的表单配置
export function fetchFormConfig(formCode) {
  const headers = getOaHeaders();
  return requestOA({
    url: '/lowcode/getformDataByFormCode',
    method: 'get',
    params: { formCode },
    headers,
  });
}

// 提交低代码流程（发起流程）
export function startLowcodeFlow(submitData) {
  const headers = getOaHeaders();
  return requestOA({
    url: '/bpmnConf/process/buttonsOperation',
    method: 'post',
    params: { formCode: submitData.formCode },
    data: submitData,
    headers,
  });
}

// OA 待办统计（与 ruoyi-mate ruoyi-ui-vue3 中 getTodoList 同一个接口）
export async function fetchTodoStats() {
  const headers = getOaHeaders();
  const resp = await requestOA({
    url: '/bpmnConf/todoList',
    method: 'get',
    headers,
  });
  const normalized = normalizeOaResponse(resp);
  return {
    code: normalized.code,
    msg: normalized.msg,
    data: normalized.data,
  };
}

// OA 低代码流程列表（与 ruoyi-mate ruoyi-ui-vue3 中 getLFActiveFormCodePageList 同一个接口）
export async function fetchActiveFlows() {
  const headers = getOaHeaders();
  const body = {
    pageDto: { page: 1, pageSize: 28 },
    taskMgmtVO: { processState: 1, description: undefined }
  };

  const res = await requestOA({
    url: '/lowcode/getLFActiveFormCodePageList',
    method: 'post',
    data: body,
    headers,
  });

  return normalizeListResponse(res);
}

export async function fetchOaWorkbenchStatus() {
  const headers = getOaHeaders();
  const resp = await requestOA({
    url: '/im/user/oa/current-status',
    method: 'get',
    headers,
    silent: true,
  });
  const normalized = normalizeOaResponse(resp);
  const status = normalized.data || {};
  return {
    code: normalized.code,
    data: {
      oaEnabled: !!status.oaEnabled,
      sysUserStatus: status.sysUserStatus,
    },
  };
}

export async function fetchTaskList(mode, pageDto, taskMgmtVO) {
  const headers = getOaHeaders();
  const modePathMap = {
    todo: 5,
    done: 4,
    started: 3,
    copy: 9,
  };

  const type = modePathMap[mode];
  if (!type) {
    throw new Error(`未知的 OA 列表模式: ${mode}`);
  }

  const res = await requestOA({
    url: `/bpmnConf/process/listPage/${type}`,
    method: 'post',
    data: {
      pageDto,
      taskMgmtVO,
    },
    headers,
  });

  return normalizeListResponse(res);
}

export async function fetchApprovalDetail(taskInfo) {
  const headers = getOaHeaders();
  const formCode = taskInfo && taskInfo.formCode;

  return requestOA({
    url: '/bpmnConf/process/viewBusinessProcess',
    method: 'post',
    params: { formCode },
    data: {
      formCode,
      processNumber: taskInfo && taskInfo.processNumber,
      taskId: taskInfo && taskInfo.taskId,
      isOutSideAccessProc: (taskInfo && taskInfo.isOutSideAccess) || false,
      isLowCodeFlow: (taskInfo && taskInfo.isLowCodeFlow) || false,
      type: 2,
    },
    headers,
  });
}

export async function fetchApprovalRecords(processNumber) {
  const headers = getOaHeaders();

  return requestOA({
    url: '/bpmnConf/getBpmVerifyInfoVos',
    method: 'get',
    params: { processNumber },
    headers,
  });
}

export async function fetchFlowPreview(taskInfo) {
  const headers = getOaHeaders();

  return requestOA({
    url: '/bpmnConf/startPagePreviewNode',
    method: 'post',
    data: {
      processNumber: taskInfo && taskInfo.processNumber,
      isStartPreview: false,
      isOutSideAccessProc: (taskInfo && taskInfo.isOutSideAccess) || false,
      isLowCodeFlow: (taskInfo && taskInfo.isLowCodeFlow) || false,
    },
    headers,
  });
}

export async function submitApprovalTask(taskInfo, submitData) {
  const headers = getOaHeaders();
  const formCode = taskInfo && taskInfo.formCode;

  return requestOA({
    url: '/bpmnConf/process/buttonsOperation',
    method: 'post',
    params: { formCode },
    data: {
      formCode,
      processNumber: taskInfo && taskInfo.processNumber,
      taskId: taskInfo && taskInfo.taskId,
      isOutSideAccess: (taskInfo && taskInfo.isOutSideAccess) || false,
      isLowCodeFlow: (taskInfo && taskInfo.isLowCodeFlow) || false,
      approvalComment: submitData && submitData.approvalComment,
      operationType: submitData && submitData.operationType,
      lfFields: (submitData && submitData.lfFields) || {},
      ...(submitData && submitData.extra ? submitData.extra : {}),
    },
    headers,
  });
}
